// =============================================================================
// PLATFORM ROLES
// =============================================================================

export type PlatformRole = "admin" | "moderator" | "developer";

export type PlanPermission =
  | "plan:advanced_nodes"
  | "plan:advanced_settings"
  | "plan:api_access"
  | "plan:branding_removal"
  | "plan:dedicated_support"
  | "plan:priority_support"
  | "plan:seasons"
  | "plan:visual_editor";

export const PLATFORM_ROLE_HIERARCHY: Record<PlatformRole, number> = {
  admin: 3,
  moderator: 2,
  developer: 1,
};

export const PLATFORM_ROLES = ["admin", "moderator", "developer"] as const;

/**
 * Check if a user's platform role meets the minimum required role.
 * Returns true if userRole rank >= requiredRole rank.
 */
export function hasPlatformRole(
  userRole: PlatformRole | null | undefined,
  requiredRole: PlatformRole,
): boolean {
  if (!userRole) return false;
  return PLATFORM_ROLE_HIERARCHY[userRole] >= PLATFORM_ROLE_HIERARCHY[requiredRole];
}

// =============================================================================
// COMMON ENUMS
// =============================================================================

export type UserStatus = "online" | "offline" | "in_game" | "in_queue";
export type ClubPrivacy = "public" | "private" | "invite_only";
export type ClubRole = "owner" | "admin" | "moderator" | "member";
export type TeamFormationMethod = "auto_balance" | "captains" | "random" | "self_select";
export type HostSelectionMethod = "highest_elo" | "random" | "first_accept";
export type StartingEloMethod = "flat" | "placement";
export type MapSelectionMethod = "vote" | "ban" | "random";
export type ChannelType = "general" | "lfg" | "custom";
export type NotificationType =
  | "match_found"
  | "match_cancelled"
  | "party_invite"
  | "party_joined"
  | "club_invite"
  | "club_join_request"
  | "club_join_approved"
  | "club_join_rejected"
  | "club_promoted"
  | "club_kicked"
  | "queue_ban"
  | "match_result"
  | "dispute_opened"
  | "dispute_resolved";

export type MatchStatus =
  | "pending_accept"
  | "accepted"
  | "in_flow"
  | "in_progress"
  | "completed"
  | "cancelled";
export type AcceptStatus = "pending" | "accepted" | "declined";
export type DisputeStatus = "open" | "under_review" | "resolved";
export type DisputeResolution = "upheld" | "overturned" | "cancelled";
export type DisputeCategory = "wrong_result" | "cheating" | "abandonment" | "technical" | "other";
export type QueueBanReason = "no_show" | "declined" | "abuse" | "other";
export type InviteStatus = "pending" | "accepted" | "declined" | "expired";
export type ApplicationStatus = "pending" | "approved" | "rejected";

// =============================================================================
// USER GAME ACCOUNT TYPES
// =============================================================================

export type UserGameAccount = {
  id: string;
  userId: string;
  gameId: string;
  username: string;
  isVerified: boolean;
  providerAccountId: string | null;
  createdAt: Date;
  verifiedAt: Date | null;
};

export type UserGameAccountWithGame = UserGameAccount & {
  game: Game;
};

// =============================================================================
// GAME TYPES
// =============================================================================

export type Game = {
  id: string;
  name: string;
  iconUrl: string | null;
  defaultTeamSize: number;
  hasRoles: boolean;
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
};

export type GameMode = {
  id: string;
  gameId: string;
  name: string;
  description: string | null;
  teamSize: number | null;
  isRanked: boolean;
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
};

export type GameMap = {
  id: string;
  gameId: string;
  name: string;
  imageUrl: string | null;
  thumbnailUrl: string | null;
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
};

export type GameModeWithMaps = GameMode & {
  maps: GameMap[];
};

export type GameWithModesAndMaps = Game & {
  modes: GameMode[];
  maps: GameMap[];
};

// =============================================================================
// USER TYPES
// =============================================================================

export type User = {
  id: string;
  username: string;
  email: string;
  bio: string | null;
  avatarUrl: string | null;
  status: UserStatus;
  lastOnlineAt: Date | null;
  isPublic: boolean;
  createdAt: Date;
  updatedAt: Date;
};

export type PublicUserProfile = {
  id: string;
  username: string;
  bio: string | null;
  avatarUrl: string | null;
  status: UserStatus;
  lastOnlineAt: Date | null;
  isPublic: boolean;
  createdAt: Date;
};

// =============================================================================
// CLUB TYPES
// =============================================================================

export type Club = {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  avatarUrl: string | null;
  bannerUrl: string | null;
  privacy: ClubPrivacy;
  ownerId: string;
  discordUrl: string | null;
  twitterUrl: string | null;
  websiteUrl: string | null;
  startingEloMethod: StartingEloMethod;
  placementMatchCount: number;
  defaultAcceptTimeout: number;
  defaultNoShowTimeout: number;
  defaultHostSelection: HostSelectionMethod;
  defaultTeamFormation: TeamFormationMethod;
  maxMembers: number;
  applicationQuestions: string[] | null;
  noShowBanDuration: number;
  banEscalationMultiplier: number;
  disputeWindowHours: number | null;
  createdAt: Date;
  updatedAt: Date;
};

export type ClubGame = {
  id: string;
  clubId: string;
  gameId: string | null; // null = fully custom game
  name: string;
  description: string | null;
  iconUrl: string | null;
  teamSize: number | null;
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
};

export type ClubGameWithBase = ClubGame & {
  game: Game | null; // Base game for defaults (null = fully custom)
};

export type ClubWithGames = Club & {
  clubGames: ClubGameWithBase[];
};

export type PublicClubProfile = {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  avatarUrl: string | null;
  bannerUrl: string | null;
  privacy: ClubPrivacy;
  games: Game[];
  discordUrl: string | null;
  twitterUrl: string | null;
  websiteUrl: string | null;
  memberCount: number;
  onlineCount: number;
  createdAt: Date;
};

export type ClubMember = {
  id: string;
  clubId: string;
  userId: string;
  role: ClubRole;
  joinedAt: Date;
};

export type ClubMemberWithUser = ClubMember & {
  user: PublicUserProfile;
};

// =============================================================================
// STATS TYPES
// =============================================================================

export type ClubMemberStats = {
  id: string;
  clubId: string;
  userId: string;
  clubGameId: string;
  queueId: string;
  mu: number;
  sigma: number;
  conservativeRating: number | null; // Generated column
  displayElo: number;
  wins: number;
  losses: number;
  draws: number;
  winStreak: number;
  lossStreak: number;
  matchesPlayed: number;
  isPlacement: boolean;
  createdAt: Date;
  updatedAt: Date;
};

export type ClubMemberStatsWithGame = ClubMemberStats & {
  clubGame: ClubGameWithBase;
};

// =============================================================================
// QUEUE TYPES
// =============================================================================

// Matchmaking flow structure TBD - will be a graph of nodes/edges
// For now, stored as generic JSON
export type MatchmakingFlow = Record<string, unknown>;

type DeepPartial<T> = {
  [P in keyof T]?: T[P] extends object ? DeepPartial<T[P]> : T[P];
};

export type PartialMatchmakingConfig = DeepPartial<MatchmakingConfig>;

export type MatchmakingConfig = {
  eloWindow: {
    baseRange: number;
    expansionStep: number;
    maxDisparity: number;
    expansionIntervalSeconds: number;
  };
  quality: {
    minMatchQuality: number;
    scoringEloWeight: number;
    scoringWaitWeight: number;
    qualityDivisor: number;
  };
  party: {
    ratingHighestWeight: number;
    ratingAverageWeight: number;
  };
  composition: {
    slots: { role: string; count: number }[];
  } | null;
  trueskill: {
    defaultMu: number;
    defaultSigma: number;
    beta: number;
    tau: number;
    drawProbability: number;
    displayEloMultiplier: number;
  };
  bans: {
    baseDurationSeconds: number;
    escalationMultiplier: number;
    historyWindowHours: number;
    maxBanDurationSeconds: number;
  };
  reporting: {
    timeoutSeconds: number;
  };
};

export type Queue = {
  id: string;
  clubId: string;
  clubGameId: string;
  gameModeId: string | null;
  name: string;
  description: string | null;
  teamSize: number | null;
  matchSize: number | null;
  teamCount: number;
  matchmakingConfig: PartialMatchmakingConfig | null;
  eloMin: number | null;
  eloMax: number | null;
  maxPartySize: number | null;
  partyEloDisparity: number | null;
  allowFullTeams: boolean;
  acceptTimeout: number | null;
  noShowTimeout: number | null;
  hostSelection: HostSelectionMethod | null;
  teamFormation: TeamFormationMethod | null;
  matchmakingFlow: MatchmakingFlow | null;
  mapPool: string[] | null;
  isEnabled: boolean;
  createdAt: Date;
  updatedAt: Date;
};

export type QueueWithGame = Queue & {
  clubGame: ClubGameWithBase;
  gameMode: GameMode | null;
};

export type QueueWithCounts = QueueWithGame & {
  playersInQueue: number;
  estimatedWait: number | null;
};

export type QueueWithMaps = QueueWithGame & {
  maps: GameMap[];
};

// =============================================================================
// PARTY TYPES
// =============================================================================

export type Party = {
  id: string;
  clubId: string;
  leaderId: string;
  code: string;
  isOpen: boolean;
  createdAt: Date;
  disbandedAt: Date | null;
};

export type PartyMember = {
  id: string;
  partyId: string;
  userId: string;
  joinedAt: Date;
};

export type PartyWithMembers = Party & {
  members: (PartyMember & { user: PublicUserProfile })[];
};

// =============================================================================
// CHAT TYPES
// =============================================================================

export type ChatChannel = {
  id: string;
  // Exactly one of these will be set
  matchId: string | null;
  partyId: string | null;
  name: string;
  type: ChannelType;
  isReadOnly: boolean;
  slowModeSeconds: number;
  createdAt: Date;
  updatedAt: Date;
};

export type ChatMessage = {
  id: string;
  channelId: string;
  userId: string;
  content: string;
  isDeleted: boolean;
  createdAt: Date;
  updatedAt: Date;
};

export type ChatMessageWithUser = ChatMessage & {
  user: PublicUserProfile;
};

// =============================================================================
// NOTIFICATION TYPES
// =============================================================================

export type Notification = {
  id: string;
  userId: string;
  type: NotificationType;
  title: string;
  body: string | null;
  data: Record<string, unknown> | null;
  isRead: boolean;
  createdAt: Date;
};

// =============================================================================
// QUEUE ENTRY TYPES
// =============================================================================

export type QueueEntry = {
  id: string;
  queueId: string;
  userId: string | null; // Solo player
  partyId: string | null; // Party
  roles: string[] | null;
  region: string | null;
  joinedAt: Date;
};

export type QueueEntryWithUser = QueueEntry & {
  user: PublicUserProfile | null;
  party: PartyWithMembers | null;
};

// =============================================================================
// MATCH TYPES
// =============================================================================

export type MatchFlowStatus =
  | "pending"
  | "running"
  | "completed"
  | "failed"
  | "cancelled"
  | "skipped";

export type Match = {
  id: string;
  queueId: string;
  clubId: string;
  clubGameId: string;
  status: MatchStatus;
  matchFlowStatus: MatchFlowStatus | null;
  mapId: string | null;
  hostId: string | null;
  teamFormation: TeamFormationMethod;
  winningTeam: number | null;
  resultData: Record<string, unknown> | null;
  createdAt: Date;
  acceptDeadline: Date | null;
  reportDeadline: Date | null;
  matchFlowStartedAt: Date | null;
  matchFlowCompletedAt: Date | null;
  startedAt: Date | null;
  completedAt: Date | null;
};

export type MatchPlayer = {
  id: string;
  matchId: string;
  userId: string;
  team: number;
  acceptStatus: AcceptStatus;
  acceptedAt: Date | null;
  isHost: boolean;
  isCaptain: boolean;
  eloAtMatch: number;
  eloChange: number | null;
  muAtMatch: number | null;
  sigmaAtMatch: number | null;
  muAfterMatch: number | null;
  sigmaAfterMatch: number | null;
  reportedWinner: number | null;
  createdAt: Date;
};

export type MatchPlayerWithUser = MatchPlayer & {
  user: PublicUserProfile;
};

export type MatchWithDetails = Match & {
  queue: Queue;
  clubGame: ClubGameWithBase;
  map: GameMap | null;
  host: PublicUserProfile | null;
  players: MatchPlayerWithUser[];
};

// =============================================================================
// DISPUTE TYPES
// =============================================================================

export type Dispute = {
  id: string;
  matchId: string;
  reporterId: string;
  category: DisputeCategory;
  reason: string;
  status: DisputeStatus;
  resolution: DisputeResolution | null;
  claimedById: string | null;
  claimedAt: Date | null;
  resolvedById: string | null;
  adminNotes: string | null;
  withdrawnAt: Date | null;
  createdAt: Date;
  resolvedAt: Date | null;
};

export type DisputeWithDetails = Dispute & {
  match: Match;
  reporter: PublicUserProfile;
  resolvedBy: PublicUserProfile | null;
};

// =============================================================================
// BAN TYPES
// =============================================================================

export type ClubBan = {
  id: string;
  clubId: string;
  userId: string;
  bannedById: string;
  reason: string | null;
  expiresAt: Date | null;
  createdAt: Date;
};

export type ClubBanWithUser = ClubBan & {
  user: PublicUserProfile;
  bannedBy: PublicUserProfile;
};

export type QueueBan = {
  id: string;
  clubId: string;
  userId: string;
  reason: QueueBanReason;
  details: string | null;
  expiresAt: Date;
  createdAt: Date;
};

export type QueueBanWithUser = QueueBan & {
  user: PublicUserProfile;
};

// =============================================================================
// CLUB INVITE TYPES
// =============================================================================

export type ClubInvite = {
  id: string;
  clubId: string;
  inviterId: string;
  inviteeId: string;
  status: InviteStatus;
  expiresAt: Date | null;
  createdAt: Date;
};

export type ClubInviteWithDetails = ClubInvite & {
  club: PublicClubProfile;
  inviter: PublicUserProfile;
  invitee: PublicUserProfile;
};

// =============================================================================
// CLUB APPLICATION TYPES
// =============================================================================

export type ClubApplication = {
  id: string;
  clubId: string;
  userId: string;
  answers: string[] | null;
  status: ApplicationStatus;
  reviewedById: string | null;
  reviewNotes: string | null;
  createdAt: Date;
  reviewedAt: Date | null;
};

export type ClubApplicationWithUser = ClubApplication & {
  user: PublicUserProfile;
  reviewedBy: PublicUserProfile | null;
};

// =============================================================================
// RANK TYPES
// =============================================================================

export type Rank =
  | "unranked"
  | "bronze"
  | "silver"
  | "gold"
  | "platinum"
  | "diamond"
  | "prime"
  | "champion";

export type RankLevel = 0 | 1 | 2 | 3 | 4;

export const DEFAULT_RANK_THRESHOLDS: Record<Rank, number> = {
  unranked: 0,
  bronze: 100,
  silver: 400,
  gold: 800,
  platinum: 1200,
  diamond: 1600,
  prime: 2000,
  champion: 2400,
};

export const RANKS_WITH_LEVELS: Rank[] = ["bronze", "silver", "gold", "platinum", "diamond"];

export function getRankFromElo(elo: number, thresholds = DEFAULT_RANK_THRESHOLDS): Rank {
  const sortedRanks = Object.entries(thresholds).sort(([, a], [, b]) => b - a) as [Rank, number][];

  for (const [rank, threshold] of sortedRanks) {
    if (elo >= threshold) {
      return rank;
    }
  }
  return "unranked";
}

export function getRankLevel(
  elo: number,
  rank: Rank,
  thresholds = DEFAULT_RANK_THRESHOLDS,
): RankLevel {
  if (!RANKS_WITH_LEVELS.includes(rank)) {
    return 0;
  }

  const currentThreshold = thresholds[rank];
  const sortedRanks = Object.entries(thresholds).sort(([, a], [, b]) => a - b) as [Rank, number][];

  const currentIndex = sortedRanks.findIndex(([r]) => r === rank);
  const nextRank = sortedRanks[currentIndex + 1];

  if (!nextRank) return 4;

  const nextThreshold = nextRank[1];
  const eloInRank = elo - currentThreshold;
  const eloPerLevel = (nextThreshold - currentThreshold) / 4;

  return Math.min(4, Math.floor(eloInRank / eloPerLevel) + 1) as RankLevel;
}

export const RANK_ORDER: Rank[] = [
  "unranked",
  "bronze",
  "silver",
  "gold",
  "platinum",
  "diamond",
  "prime",
  "champion",
];

export type QueueRankConfig = {
  thresholds?: Partial<Record<string, number>>;
  primeTopN?: number | null;
  championTopN?: number | null;
};

export function getMergedThresholds(rankConfig?: QueueRankConfig | null): Record<Rank, number> {
  const merged = { ...DEFAULT_RANK_THRESHOLDS };
  if (rankConfig?.thresholds) {
    for (const [key, value] of Object.entries(rankConfig.thresholds)) {
      if (key in merged && typeof value === "number") {
        merged[key as Rank] = value;
      }
    }
  }
  return merged;
}

/**
 * Resolve the effective rank and division for a player given their ELO,
 * leaderboard position, and the queue's rank configuration.
 * Position overrides only promote, never demote.
 */
export function resolveRank(
  elo: number,
  position: number | null,
  rankConfig?: QueueRankConfig | null,
): { rank: Rank; division: RankLevel } {
  const thresholds = getMergedThresholds(rankConfig);
  let rank = getRankFromElo(elo, thresholds);

  if (position != null && position >= 1) {
    let positionRank: Rank | null = null;
    if (rankConfig?.championTopN && position <= rankConfig.championTopN) {
      positionRank = "champion";
    } else if (rankConfig?.primeTopN && position <= rankConfig.primeTopN) {
      positionRank = "prime";
    }

    if (positionRank) {
      const eloIdx = RANK_ORDER.indexOf(rank);
      const posIdx = RANK_ORDER.indexOf(positionRank);
      if (posIdx > eloIdx) rank = positionRank;
    }
  }

  const division = getRankLevel(elo, rank, thresholds);
  return { rank, division };
}

/**
 * Resolve the effective rank (without division) for display purposes.
 */
export function resolveEffectiveRank(
  elo: number,
  position: number | null,
  rankConfig?: QueueRankConfig | null,
): Rank {
  return resolveRank(elo, position, rankConfig).rank;
}

// =============================================================================
// PLAN LIMITS
// =============================================================================

export type PlanLimits = {
  maxMembers: number;
  maxQueues: number;
  maxPlayersPerQueue: number;
  permissions: PlanPermission[];
  hasVisualEditor: boolean;
  hasSeasons: boolean;
  hasBrandingRemoval: boolean;
  hasAdvancedSettings: boolean;
  hasApiAccess: boolean;
  hasPrioritySupport: boolean;
  hasDedicatedSupport: boolean;
};

// =============================================================================
// MATCH FLOW TYPES
// =============================================================================

export * from "./match-flow/index.js";
export * from "./permissions.js";

// =============================================================================
// DISCORD BOT TYPES
// =============================================================================

export * from "./discord/index.js";
